import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { pinHtml } from "@/lib/categories";

export default function MapCanvas({ items, onMoveEnd, onSelect, selectedId, focus, fitAll, panTo, dimUnselected, cluster, onBackgroundClick, onClusterClick, hull, pinHtmlFor, renderSig, popupHtml, popupAge, className }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const fittedRef = useRef(false);
  const moveCbRef = useRef(onMoveEnd);
  const selectCbRef = useRef(onSelect);
  const itemsRef = useRef(items);

  moveCbRef.current = onMoveEnd;
  selectCbRef.current = onSelect;
  itemsRef.current = items;

  const markersRef = useRef(new Map());
  const selectedRef = useRef(selectedId);
  const prevSelRef = useRef(null);
  const bgClickRef = useRef(onBackgroundClick);
  const clusterClickRef = useRef(onClusterClick);
  const hullRef = useRef(null);
  const pinHtmlForRef = useRef(pinHtmlFor);
  const popupRef = useRef(null);
  const popupAgeRef = useRef(popupAge);
  selectedRef.current = selectedId;
  bgClickRef.current = onBackgroundClick;
  clusterClickRef.current = onClusterClick;
  pinHtmlForRef.current = pinHtmlFor;
  popupAgeRef.current = popupAge;

  const iconFor = (it, active) => {
    const html = pinHtmlForRef.current
      ? pinHtmlForRef.current(it, active)
      : pinHtml(it.category, active);
    return L.divIcon({
      html,
      className: "go-pin-wrap",
      iconSize: [32, 32],
      iconAnchor: [16, 30],
    });
  };

  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map(elRef.current, {
      zoomControl: false,
      attributionControl: true,
      preferCanvas: false,
    }).setView([51.1657, 10.4515], 6);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);

    layerRef.current = (
      cluster
        ? L.markerClusterGroup({
            maxClusterRadius: 55,
            disableClusteringAtZoom: 18,
            spiderfyOnMaxZoom: false,
            showCoverageOnHover: false,
            chunkedLoading: true,
            removeOutsideVisibleBounds: true,
            iconCreateFunction: (c) => {
              const count = c.getChildCount();
              const size = count < 10 ? 36 : count < 50 ? 44 : count < 200 ? 52 : 60;
              return L.divIcon({
                html: `<div class="go-cluster" style="width:${size}px;height:${size}px">${count}</div>`,
                className: "go-cluster-wrap",
                iconSize: [size, size],
              });
            },
          })
        : L.layerGroup()
    ).addTo(map);
    mapRef.current = map;

    if (focus && focus.lat != null) {
      fittedRef.current = true;
      map.setView([focus.lat, focus.lng], 18);
    }

    const emit = () => {
      if (!map._loaded) return;
      const b = map.getBounds();
      moveCbRef.current &&
        moveCbRef.current({
          north: b.getNorth(),
          south: b.getSouth(),
          east: b.getEast(),
          west: b.getWest(),
        });
    };
    map.on("moveend", emit);
    map.on("click", () => bgClickRef.current && bgClickRef.current());
    map.on("popupclose", (e) => {
      if (e.popup === popupRef.current) {
        popupRef.current = null;
        bgClickRef.current && bgClickRef.current();
      }
    });
    if (cluster) {
      layerRef.current.on("clusterclick", () => {
        if (clusterClickRef.current) clusterClickRef.current();
        // Safety net: force the list to reflect the new viewport after the
        // zoom-to-bounds animation finishes (in case moveend was missed).
        setTimeout(emit, 450);
      });
    }
    const onResize = () => {
      if (!map._loaded) return;
      map.invalidateSize();
      emit();
    };
    window.addEventListener("resize", onResize);
    const initTimer = setTimeout(() => {
      if (!map._loaded) return;
      map.invalidateSize();
      emit();
    }, 150);

    return () => {
      clearTimeout(initTimer);
      window.removeEventListener("resize", onResize);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    markersRef.current.clear();
    const pts = [];
    (items || []).forEach((it) => {
      if (it.latitude == null || it.longitude == null) return;
      const m = L.marker([it.latitude, it.longitude], {
        icon: iconFor(it, it.permid === selectedRef.current),
      });
      m.on("click", () => selectCbRef.current && selectCbRef.current(it));
      if (it.permid === selectedRef.current) m.setZIndexOffset(1000);
      layer.addLayer(m);
      markersRef.current.set(it.permid, { marker: m, item: it });
      pts.push([it.latitude, it.longitude]);
    });
    if (!fittedRef.current && pts.length) {
      fittedRef.current = true;
      if (pts.length === 1) map.setView(pts[0], 16);
      else map.fitBounds(pts, { padding: [70, 70], maxZoom: 17 });
    }
  }, [items]);

  // Only re-icon the affected markers when the selection changes (avoids a full
  // rebuild of hundreds of markers on every card/pin click).
  useEffect(() => {
    const mk = markersRef.current;
    const setIconFor = (permid, active) => {
      const entry = mk.get(permid);
      if (!entry) return;
      entry.marker.setIcon(iconFor(entry.item, active));
    };
    if (prevSelRef.current && prevSelRef.current !== selectedId) {
      setIconFor(prevSelRef.current, false);
      const prev = mk.get(prevSelRef.current);
      if (prev) prev.marker.setZIndexOffset(0);
    }
    if (selectedId) {
      setIconFor(selectedId, true);
      const cur = mk.get(selectedId);
      if (cur) cur.marker.setZIndexOffset(1000);
    }
    prevSelRef.current = selectedId;

    const map = mapRef.current;
    if (map && dimUnselected) {
      map.getContainer().classList.toggle("go-dim-selection", !!selectedId);
    }
  }, [selectedId, dimUnselected]);

  // Re-icon every marker when IoT states change (state signature bump).
  useEffect(() => {
    if (renderSig == null) return;
    const mk = markersRef.current;
    mk.forEach((entry, permid) => {
      entry.marker.setIcon(iconFor(entry.item, permid === selectedRef.current));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderSig]);

  // Anchored detail popup at the selected item's location (moves with the map).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const it = (itemsRef.current || []).find((i) => i.permid === selectedId);
    if (!popupHtml || !it || it.latitude == null) {
      if (popupRef.current) {
        map.closePopup(popupRef.current);
        popupRef.current = null;
      }
      return;
    }
    if (popupRef.current) {
      popupRef.current
        .setLatLng([it.latitude, it.longitude])
        .setContent(popupHtml);
    } else {
      popupRef.current = L.popup({
        autoClose: false,
        closeOnClick: false,
        autoPan: false,
        className: "go-mappopup",
        maxWidth: 232,
        minWidth: 200,
        offset: [0, -24],
      })
        .setLatLng([it.latitude, it.longitude])
        .setContent(popupHtml)
        .openOn(map);
    }
    // Populate the live-age span immediately (content was just (re)built).
    const el = popupRef.current.getElement && popupRef.current.getElement();
    const span = el && el.querySelector(".go-popup-age");
    if (span) span.textContent = popupAgeRef.current || "";
  }, [popupHtml, selectedId]);

  // Live-tick only the age span so the image is never re-fetched.
  useEffect(() => {
    const p = popupRef.current;
    if (!p) return;
    const el = p.getElement && p.getElement();
    if (!el) return;
    const span = el.querySelector(".go-popup-age");
    if (span) span.textContent = popupAge || "";
  }, [popupAge]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus || focus.lat == null) return;
    fittedRef.current = true;
    map.setView([focus.lat, focus.lng], 18, { animate: false });
  }, [focus]);

  useEffect(() => {
    if (!fitAll) return;
    const map = mapRef.current;
    if (!map) return;
    const pts = (itemsRef.current || [])
      .filter((i) => i.latitude != null && i.longitude != null)
      .map((i) => [i.latitude, i.longitude]);
    if (pts.length === 1) map.setView(pts[0], 16);
    else if (pts.length) map.fitBounds(pts, { padding: [70, 70], maxZoom: 17 });
  }, [fitAll]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !panTo || panTo.lat == null) return;
    fittedRef.current = true;
    // keepZoom: pan/center the pin without changing the current zoom level.
    const z = panTo.keepZoom ? map.getZoom() : panTo.zoom || 17;
    map.flyTo([panTo.lat, panTo.lng], z, { duration: 0.6 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panTo]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (hullRef.current) {
      hullRef.current.remove();
      hullRef.current = null;
    }
    if (hull && hull.length >= 3) {
      hullRef.current = L.polygon(hull, {
        color: "#dc2626",
        weight: 3,
        opacity: 0.9,
        fill: true,
        fillColor: "#dc2626",
        fillOpacity: 0.05,
        interactive: false,
      }).addTo(map);
    }
  }, [hull]);

  return <div ref={elRef} className={className} data-testid="map-container" />;
}
